import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Grid,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Warning as WarningIcon,
    Schedule as ScheduleIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import subscriptionApi from '../services/subscriptionApi';
import dayjs from 'dayjs';
import * as Sentry from "@sentry/react";
import { normalizeSubscriptionStatus } from '../utils/subscriptionUtils';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount / 100);
};

const formatCredits = (credits) => {
    return new Intl.NumberFormat('en-US').format(credits);
};

const getStatusColor = (status) => {
    const normalizedStatus = normalizeSubscriptionStatus(status);
    switch (normalizedStatus) {
        case 'active':
            return 'success';
        case 'trialing':
            return 'info';
        case 'past_due':
            return 'warning';
        case 'canceled':
        case 'incomplete_expired':
            return 'error';
        case 'paused':
            return 'default';
        default:
            return 'default';
    }
};

const getStatusIcon = (status) => {
    const normalizedStatus = normalizeSubscriptionStatus(status);
    switch (normalizedStatus) {
        case 'active':
            return <CheckCircleIcon />;
        case 'trialing':
            return <ScheduleIcon />;
        case 'past_due':
            return <WarningIcon />;
        case 'canceled':
        case 'incomplete_expired':
            return <CancelIcon />;
        case 'paused':
            return <ScheduleIcon />;
        default:
            return <ScheduleIcon />;
    }
};



const getStatusText = (status) => {
    const normalizedStatus = normalizeSubscriptionStatus(status);
    switch (normalizedStatus) {
        case 'active':
            return 'Active';
        case 'trialing':
            return 'Trial';
        case 'past_due':
            return 'Past Due';
        case 'canceled':
            return 'Canceled';
        case 'incomplete_expired':
            return 'Expired';
        case 'paused':
            return 'Paused';
        default:
            return normalizedStatus;
    }
};

const SubscriptionManagement = ({ subscriptions = [], onSubscriptionUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [cancelDialog, setCancelDialog] = useState(null);
    const [reactivateDialog, setReactivateDialog] = useState(null);
    const [error, setError] = useState(null);

    const handleCancelSubscription = async (subscription) => {
        try {
            setLoading(true);
            setError(null);
            
            await subscriptionApi.cancelSubscription(subscription.id);
            
            if (onSubscriptionUpdate) {
                await onSubscriptionUpdate();
            }
            
            setCancelDialog(null);
        } catch (error) {
            Sentry.captureException(error);
            setError(error.message || 'Failed to cancel subscription');
            console.error('Error canceling subscription:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleReactivateSubscription = async (subscription) => {
        try {
            setLoading(true);
            setError(null);
            
            await subscriptionApi.reactivateSubscription(subscription.id);
            
            if (onSubscriptionUpdate) {
                await onSubscriptionUpdate();
            }
            
            setReactivateDialog(null);
        } catch (error) {
            Sentry.captureException(error);
            setError(error.message || 'Failed to reactivate subscription');
            console.error('Error reactivating subscription:', error);
        } finally {
            setLoading(false);
        }
    };

    if (subscriptions.length === 0) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                You don't have any active subscriptions yet.
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Your Subscriptions
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {subscriptions.map((subscription) => (
                    <Grid item xs={12} md={6} key={subscription.id}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {subscription.plan?.name || 'Unknown Plan'}
                                    </Typography>
                                    <Chip
                                        icon={getStatusIcon(subscription.status)}
                                        label={getStatusText(subscription.status)}
                                        color={getStatusColor(subscription.status)}
                                        size="small"
                                    />
                                </Box>

                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                                    {formatCurrency(subscription.plan?.price_cents || 0)}
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        /month
                                    </Typography>
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {formatCredits(subscription.plan?.monthly_credits || 0)} credits per month
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <List dense>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText 
                                            primary="Current Period"
                                            secondary={`${dayjs(subscription.current_period_start).format('MMM D, YYYY')} - ${dayjs(subscription.current_period_end).format('MMM D, YYYY')}`}
                                        />
                                    </ListItem>
                                    
                                    {normalizeSubscriptionStatus(subscription.status) === 'active' && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText 
                                                primary="Next Billing Date"
                                                secondary={dayjs(subscription.current_period_end).format('MMM D, YYYY')}
                                            />
                                        </ListItem>
                                    )}

                                    {subscription.cancel_at_period_end && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText 
                                                primary="Cancellation Date"
                                                secondary={dayjs(subscription.current_period_end).format('MMM D, YYYY')}
                                            />
                                        </ListItem>
                                    )}
                                </List>

                                {subscription.cancel_at_period_end && (
                                    <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                        This subscription will be canceled at the end of the current period.
                                    </Alert>
                                )}

                                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {normalizeSubscriptionStatus(subscription.status) === 'active' && !subscription.cancel_at_period_end && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => setCancelDialog(subscription)}
                                            disabled={loading}
                                        >
                                            Cancel Subscription
                                        </Button>
                                    )}

                                    {(subscription.status === 'canceled' || subscription.cancel_at_period_end) && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<RefreshIcon />}
                                            onClick={() => setReactivateDialog(subscription)}
                                            disabled={loading}
                                        >
                                            Reactivate
                                        </Button>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Cancel Confirmation Dialog */}
            <Dialog
                open={!!cancelDialog}
                onClose={() => !loading && setCancelDialog(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Cancel Subscription</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to cancel your <strong>{cancelDialog?.plan_name}</strong> subscription?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Your subscription will remain active until the end of your current billing period 
                        ({dayjs(cancelDialog?.current_period_end).format('MMM D, YYYY')}). 
                        You'll still have access to all features until then.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialog(null)} disabled={loading}>
                        Keep Subscription
                    </Button>
                    <Button 
                        onClick={() => handleCancelSubscription(cancelDialog)} 
                        color="error"
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Canceling...' : 'Cancel Subscription'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reactivate Confirmation Dialog */}
            <Dialog
                open={!!reactivateDialog}
                onClose={() => !loading && setReactivateDialog(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reactivate Subscription</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Reactivate your <strong>{reactivateDialog?.plan_name}</strong> subscription?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Your subscription will be reactivated immediately and you'll be charged 
                        {formatCurrency(reactivateDialog?.price_cents)} for the current billing period.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReactivateDialog(null)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => handleReactivateSubscription(reactivateDialog)} 
                        color="primary"
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Reactivating...' : 'Reactivate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SubscriptionManagement; 